import React from 'react';
import ReactDOM from 'react-dom';
import { useEditor, useNode } from '@craftjs/core';
import { useSelector } from 'react-redux';
import styles from './styles/styles.module.css';
import { useInteractionMode } from '../contexts/InteractionMode';

/** Body portal to ensure the modal sits above all stacking contexts */
function BodyPortal({ children }: { children: React.ReactNode }) {
  const el = React.useMemo(() => document.createElement('div'), []);
  React.useEffect(() => {
    document.body.appendChild(el);
    return () => {
      document.body.removeChild(el);
    };
  }, [el]);
  return ReactDOM.createPortal(children, el);
}

const MultilineTextbox = ({
  label,
  value,
  left = 0,
  top = 0,
  width = 300,
  height = 150,
  required = false, // builder-time default
}: any) => {
  const { enabled, actions } = useEditor((state) => ({
    enabled: state.options.enabled,
  }));

  const {
    connectors: { connect },
    setProp,
    id,
  } = useNode();

  const { selected } = useSelector((s: any) => s.formToggle);
  const interactionMode = useInteractionMode();

  // Local UI state
  const [isDragging, setDragging] = React.useState(false);
  const [isResizing, setResizing] = React.useState(false);
  const [showEditor, setShowEditor] = React.useState(false);

  // Drag node (BUILD/REPORT views)
  const onMouseDown = (e: React.MouseEvent) => {
    if (!enabled || showEditor || isResizing) return;
    e.preventDefault();
    setDragging(true);

    const startX = e.clientX;
    const startY = e.clientY;
    const initialLeft = left;
    const initialTop = top;

    const onMouseMove = (ev: MouseEvent) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      setProp((props: any) => {
        props.left = initialLeft + dx;
        props.top = initialTop + dy;
      });
    };

    const onMouseUp = () => {
      setDragging(false);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  // Resize node (FORM only)
  const onResizeMouseDown = (e: React.MouseEvent) => {
    if (!enabled) return;
    e.stopPropagation();
    e.preventDefault();
    setResizing(true);

    const startX = e.clientX;
    const startY = e.clientY;
    const initialWidth = width;
    const initialHeight = height;

    const onMouseMove = (ev: MouseEvent) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      setProp((props: any) => {
        props.width = Math.max(150, initialWidth + dx);
        props.height = Math.max(60, initialHeight + dy);
      });
    };

    const onMouseUp = () => {
      setResizing(false);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  // Open modal editor (FORM only)
  const onDoubleClick = () => {
    if (!enabled) return;
    if (selected === 'FORM') setShowEditor(true);
  };

  // Close on Escape when modal open
  React.useEffect(() => {
    if (!showEditor) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowEditor(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showEditor]);

  const handleDelete = () => {
    actions.delete(id);
  };

  function classes() {
    if (interactionMode === 'VIEW') {
      return {
        wrapper: styles.wrapperReport,
        label: styles.labelReport,
        container: styles.inputContainerReport,
        textarea: styles.textareaReport,
      };
    }
    return {
      wrapper: styles.wrapperForm,
      label: styles.labelForm,
      container: styles.inputContainerForm,
      textarea: styles.textareaForm,
    };
  }

  const cls = classes();
  const isView = interactionMode === 'VIEW';
  const isForm = selected === 'FORM';
  const isFill = interactionMode === 'FILL';

  return (
    <>
      <div
        ref={(ref) => ref && connect(ref)}
        className={cls.wrapper}
        style={{
          left,
          top,
          position: 'absolute',
          cursor: enabled ? (isDragging ? 'grabbing' : 'grab') : 'default',
        }}
        data-required={String(!!required)}
      >
        {interactionMode !== 'VIEW' && (
          <label className={cls.label} title={label}>
            {label}
            {required && (
              <span style={{ marginLeft: 6, color: '#c00' }} aria-hidden>
                *
              </span>
            )}
            {enabled && isForm && (
              <div
                className={styles.deleteButton}
                onClick={handleDelete}
                role="button"
                tabIndex={0}
              >
                ×
              </div>
            )}
          </label>
        )}

        {isView && (
          <div className={styles.viewValueContainer}>
            <p className={styles.viewLabel}>{label}</p>
            <p className={styles.viewValue}>{value}</p>
          </div>
        )}

        {!isView && (
          <div
            className={cls.container}
            style={{ width, height }}
            onMouseDown={onMouseDown}
            onDoubleClick={onDoubleClick}
          >
            <textarea
              className={cls.textarea}
              value={value || ''}
              required={isFill ? !!required : false}
              aria-required={isFill ? !!required : false}
              onChange={(e) =>
                interactionMode === 'FILL' &&
                setProp((props: any) => {
                  props.value = e.target.value;
                })
              }
            />
            {enabled && (
              <div
                onMouseDown={onResizeMouseDown}
                className={styles.resizeHandle}
                title="Drag to resize"
              />
            )}
          </div>
        )}
      </div>

      {/* === Centered modal editor (FORM only) === */}
      {showEditor && isForm && (
        <BodyPortal>
          <div
            className={styles.modalOverlay}
            role="presentation"
            onMouseDown={() => setShowEditor(false)} /* click backdrop closes */
          >
            <div
              className={styles.modalPanel}
              role="dialog"
              aria-modal="true"
              aria-label="Edit multiline textbox"
              onMouseDown={(e) => e.stopPropagation()} /* keep clicks inside */
            >
              <div className={styles.modalHeader}>
                <h3 className={styles.modalTitle}>Edit Textbox</h3>
                <button
                  className={styles.modalClose}
                  aria-label="Close"
                  onClick={() => setShowEditor(false)}
                >
                  ×
                </button>
              </div>

              <div className={styles.modalBody}>
                <div className={styles.formRow}>
                  <label className={styles.rowLabel}>Label</label>
                  <input
                    className={styles.rowInput}
                    type="text"
                    value={label}
                    onChange={(e) =>
                      setProp((props: any) => (props.label = e.target.value))
                    }
                  />
                </div>

                {/* New: builder-time "Required field" toggle */}
                <div
                  className={styles.formRowInline}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <input
                    id={`required-${id}`}
                    type="checkbox"
                    checked={!!required}
                    onChange={(e) =>
                      setProp((props: any) => {
                        props.required = e.target.checked;
                      })
                    }
                  />
                  <label style={{ marginLeft: 6 }} htmlFor={`required-${id}`}>
                    Required field
                  </label>
                </div>
              </div>

              <div className={styles.modalFooter}>
                <button
                  className={styles.btnPrimary}
                  onClick={() => setShowEditor(false)}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </BodyPortal>
      )}
    </>
  );
};

(MultilineTextbox as any).craft = {
  displayName: 'MultilineTextbox',
  props: {
    label: 'Report',
    value: '',
    left: 0,
    top: 0,
    width: 300,
    height: 150,
    required: false, // default off
  },
};

export default MultilineTextbox;
